import React, { useState, useRef } from "react";
// Header is provided by LayoutWithHeader in App.js
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function ChangeMapView({ coords }) {
    const map = useMap();
    React.useEffect(() => {
        map.setView(coords, 13);
    }, [coords, map]);
    return null;
}

export default function HostDinnerLocation() {
    const [address, setAddress] = useState("");
    const [coords, setCoords] = useState([37.7749, -122.4194]);
    const [markerText, setMarkerText] = useState("San Francisco");
    const [suggestions, setSuggestions] = useState([]);
    const markerRef = useRef(null);
    const navigate = useNavigate();

    // Fetch address suggestions as user types
    const handleInputChange = async (e) => {
        const value = e.target.value;
        setAddress(value);
        if (value.length < 2) {
            setSuggestions([]);
            return;
        }
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json`
            );
            if (!response.ok) throw new Error("Network response was not ok");
            const data = await response.json();
            setSuggestions(data);
        } catch (error) {
            setSuggestions([]);
        }
    };

    // When a suggestion is clicked
    const handleSuggestionClick = (suggestion) => {
        setCoords([parseFloat(suggestion.lat), parseFloat(suggestion.lon)]);
        setMarkerText(suggestion.display_name);
        setAddress(suggestion.display_name);
        setSuggestions([]);
    };

    // When marker is dragged
    const handleMarkerDragEnd = () => {
        const marker = markerRef.current;
        if (marker != null) {
            const latlng = marker.getLatLng();
            setCoords([latlng.lat, latlng.lng]);
            setMarkerText(`Lat: ${latlng.lat.toFixed(5)}, Lng: ${latlng.lng.toFixed(5)}`);
        }
    };

    return (
        <div className="container-fluid min-vh-100 d-flex flex-column p-0" style={{ background: 'none' }}>
            <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                <div className="card shadow-lg p-4 rounded-4 w-100" style={{ maxWidth: 700, background: '#fff' }}>
                    <div className="d-flex align-items-center mb-3" style={{ justifyContent: 'flex-end' }}>
                        <span role="img" aria-label="house" style={{ fontSize: 32, marginRight: 12 }}>🏠</span>
                        <span className="host-dinner-link mb-0" style={{ fontWeight: 600, color: "#15316b", marginLeft: 8, textAlign: "right", fontSize: '1.2rem' }}>Host a Dinner</span>
                    </div>
                    <h2 className="mb-2" style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                        Where is your home located?
                    </h2>
                    <div className="mb-2" style={{ color: '#555' }}>
                        Your exact address will be sent to the guest only after your confirmation
                    </div>
                    <div className="w-100 d-flex justify-content-center mb-3">
                        <div className="map-wrapper" style={{ position: 'relative', width: '90%', maxWidth: 500, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
                            <div style={{
                                position: 'absolute',
                                top: 18,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 1001,
                                width: '90%',
                                maxWidth: 420,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}>
                                <input
                                    type="text"
                                    placeholder="What's your address?"
                                    value={address}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '90%',
                                        padding: '12px 18px',
                                        borderRadius: '24px',
                                        border: '1px solid #ccc',
                                        fontSize: '1rem',
                                        background: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                                        marginBottom: suggestions.length > 0 ? 0 : 8,
                                    }}
                                    autoComplete="off"
                                />
                                {suggestions.length > 0 && (
                                    <ul style={{
                                        position: 'absolute',
                                        top: 48,
                                        left: 0,
                                        right: 0,
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '0 0 16px 16px',
                                        zIndex: 1002,
                                        maxHeight: 180,
                                        overflowY: 'auto',
                                        listStyle: 'none',
                                        margin: 0,
                                        padding: 0,
                                        width: '100%'
                                    }}>
                                        {suggestions.map((s, idx) => (
                                            <li
                                                key={s.place_id}
                                                style={{
                                                    padding: '10px 18px',
                                                    cursor: 'pointer',
                                                    borderBottom: idx === suggestions.length - 1 ? 'none' : '1px solid #eee',
                                                    background: '#fff',
                                                    fontSize: '1rem',
                                                    textAlign: 'left',
                                                }}
                                                onClick={() => handleSuggestionClick(s)}
                                            >
                                                {s.display_name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <MapContainer
                                center={coords}
                                zoom={13}
                                style={{ height: '250px', width: '100%', borderRadius: '16px', filter: 'none' }}
                            >
                                <ChangeMapView coords={coords} />
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Marker
                                    position={coords}
                                    draggable={true}
                                    eventHandlers={{ dragend: handleMarkerDragEnd }}
                                    ref={markerRef}
                                >
                                    <Popup>{markerText}</Popup>
                                </Marker>
                            </MapContainer>
                        </div>
                    </div>
                    <div className="d-flex justify-content-center">
                        <button
                            className="btn btn-warning px-4 py-2 rounded-pill fw-bold text-white"
                            style={{ background: '#ffb97a', border: 'none', marginTop: 10 }}
                            onClick={() => navigate('/HostDinnerAddress', { state: { address, coords, markerText } })}
                        >
                            Next step
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}